import { useDisclosure } from "@msuite/ui/hooks";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import type { VendorArticeListItem } from "@msuite/utils/types";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { useDeleteItem } from "@/hooks/use-delete-item";
import { EditVendorArticleModal } from "@/modals/stock/edit-vendor-article-modal";

export function RowActions({ cell }: CellContext<VendorArticeListItem, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const editModal = useDisclosure();

	/** Query */
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnDeleteVendorArticle() {
		const response = await client.stock.articles[":articleId"]["vendor-articles"][
			":vendorArticleId"
		].$delete({
			param: {
				articleId: cell.row.original.articleId,
				vendorArticleId: cell.row.original.id,
			},
		});
		await responseHandler(response);
	}

	const { RenderOnDeleteModal, handleOnDelete } = useDeleteItem(handleOnDeleteVendorArticle);

	/** Computed */
	// The same three-state guard `apps/web/src/components/citadel/can.tsx` documents: the menu is
	// an array rather than a subtree, so the gate sits here, and an entry is dropped only on a
	// definitive denial — while the rules load the server keeps the last word. Editing gates on
	// `update Stock` and deleting on `delete Stock`, as their routes do.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const stockDeleteIsDenied = !!citadel && citadel.cannot("delete", "Stock");

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={null}>
				{!(stockUpdateIsDenied && stockDeleteIsDenied) && (
					<Menu
						items={createStandardMenuItems({
							onEdit: stockUpdateIsDenied ? undefined : editModal.onOpen,
							onDelete: stockDeleteIsDenied ? undefined : handleOnDelete,
						})}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			<RenderOnDeleteModal />
			{editModal.isOpen && (
				<EditVendorArticleModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					onComplete={refetch}
					articleId={cell.row.original.articleId}
					vendorArticleId={cell.row.original.id}
				/>
			)}
		</Fragment>
	);
}
